import React, { useState, useEffect } from "react";
import { getTrialBal } from "../../../api/endpoints/allReports";
import { authSelector } from "../../../store/selectors/index";
import { useSelector } from "react-redux";
import { message } from "antd";
import SideBar from "../../sidebar/sideBar";
import Header from "../../header/header";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getSiteRefrence } from "../../../api/endpoints/refrences";

const TrialBalance = () => {
  const { user, access_token } = useSelector(authSelector);

  const [startingDate, setStartingDate] = useState("");
  const [endingDate, setEndingDate] = useState("");

  const [siteName, setSiteName] = useState("");
  const [siteAddress, setSiteAddress] = useState("");

  const [itemsQueue, setItemsQueue] = useState([]);
  const [loading, setLoading] = useState(false);

  // =========================================================
  // DATE HANDLERS
  // =========================================================

  const setStartDate = (e) => {
    setStartingDate(e.target.value);
  };

  const setEndDate = (e) => {
    setEndingDate(e.target.value);
  };

  // =========================================================
  // GET SITE INFORMATION
  // =========================================================

  const getName = async () => {
    try {
      const result = await getSiteRefrence(access_token);

      if (result.success) {
        setSiteName(result.data.siteName);
        setSiteAddress(result.data.siteAddress);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      console.error(error);
      message.error("Unable to load site information!");
    }
  };

  useEffect(() => {
    getName();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================================================
  // FORMAT DATE
  // =========================================================

  const formatDate = (inputDate) => {
    if (!inputDate) return "";

    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    return new Date(inputDate).toLocaleDateString(undefined, options);
  };

  // =========================================================
  // FORMAT DATE DD/MM/YYYY
  // =========================================================

  const formatDateDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";

    const d = new Date(dateStr);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // =========================================================
  // FORMAT NUMBER
  // =========================================================

  const formatNumber = (number) => {
    const value = Number(number);

    if (isNaN(value)) {
      return "0.00";
    }

    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // =========================================================
  // VIEW TRIAL BALANCE
  // =========================================================

  const editHandler = async () => {
    if (!endingDate) {
      return message.error("Please select ending date!");
    }

    const data = {
      endingDate: endingDate,
      siteId: Number(user.siteId),
    };

    try {
      setLoading(true);

      const results = await getTrialBal(data, access_token);

      console.log(results, "Trial Balance Result");

      if (!results.success) {
        message.error(results.message || "Failed to load Trial Balance!");
        return;
      }

      if (!results.data || results.data.length === 0) {
        message.error("No Record Found in this date range!");
        return;
      }

      // =====================================================
      // PREPARE DATA
      // =====================================================

      const payload = results.data.map((curr) => ({
        generalLedgerCode: curr.generalLedgerCode,
        description: curr.generalLedgerDescription,
        debit: Number(curr.debit) || 0,
        credit: Number(curr.credit) || 0,
        siteId: curr.siteId,
      }));

      setItemsQueue(payload);

      // =====================================================
      // GENERATE PDF
      // =====================================================

      generatePDF(payload);
    } catch (error) {
      console.error(error);

      message.error("Something went wrong while loading Trial Balance!");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // CLEAR
  // =========================================================

  const clearFields = () => {
    setStartingDate("");
    setEndingDate("");
    setItemsQueue([]);
  };

  // =========================================================
  // GENERATE PDF
  // =========================================================

  const generatePDF = (data) => {
    if (!data || data.length === 0) {
      message.error("No data to generate report!");
      return;
    }

    const doc = new jsPDF("p", "mm", "a4");

    // =====================================================
    // HEADER
    // =====================================================

    const pageWidth = doc.internal.pageSize.getWidth();

    const now = new Date();

    const printDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

    // Printed date
    doc.setFontSize(8);

    doc.text(`Printed on: ${printDateTime}`, pageWidth - 10, 5, {
      align: "right",
    });

    // Site Name
    doc.setFontSize(16);

    doc.text(`${siteName || ""}`, pageWidth / 2, 11, {
      align: "center",
    });

    // Site Address
    doc.setFontSize(11);

    doc.text(`${siteAddress || ""}`, pageWidth / 2, 16, {
      align: "center",
    });

    // Date Range
    doc.setFontSize(10);

    doc.text(
      `Trial Balance  ${formatDate(endingDate)}`,
      pageWidth / 2,
      21,
      {
        align: "center",
      },
    );

    // Report Title
    doc.setFontSize(12);

    doc.setFont(undefined, "bold");

    doc.text("TRIAL BALANCE", pageWidth / 2, 27, {
      align: "center",
    });

    doc.setFont(undefined, "normal");

    // =====================================================
    // TABLE DATA
    // =====================================================

    const tableRows = [];

    let totalDebit = 0;
    let totalCredit = 0;

    // Sort by GL Code
    const sortedData = [...data].sort(
      (a, b) => Number(a.generalLedgerCode) - Number(b.generalLedgerCode),
    );

    sortedData.forEach((r) => {
      const debit = Number(r.debit) || 0;

      const credit = Number(r.credit) || 0;

      totalDebit += debit;
      totalCredit += credit;

      tableRows.push([
        r.generalLedgerCode || "",
        r.description || "",
        formatNumber(debit),
        formatNumber(credit),
      ]);
    });

    // =====================================================
    // TOTAL ROW
    // =====================================================

    tableRows.push([
      {
        content: "TOTAL",
        colSpan: 2,
        styles: {
          halign: "right",
          fontStyle: "bold",
        },
      },
      {
        content: formatNumber(totalDebit),
        styles: {
          halign: "right",
          fontStyle: "bold",
        },
      },
      {
        content: formatNumber(totalCredit),
        styles: {
          halign: "right",
          fontStyle: "bold",
        },
      },
    ]);

    // =====================================================
    // RENDER TABLE
    // =====================================================

    autoTable(doc, {
      head: [["GL Code", "Description", "Debit", "Credit"]],

      body: tableRows,

      startY: 31,

      theme: "grid",

      margin: {
        left: 10,
        right: 10,
      },

      styles: {
        fontSize: 9,
        cellPadding: 1.5,
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        valign: "middle",
      },

      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
      },

      columnStyles: {
        0: {
          halign: "center",
          cellWidth: 35,
        },

        1: {
          halign: "left",
          cellWidth: 80,
        },

        2: {
          halign: "right",
          cellWidth: 35,
        },

        3: {
          halign: "right",
          cellWidth: 35,
        },
      },

      didParseCell: (hookData) => {
        // Bold total row
        if (hookData.row.index === tableRows.length - 1) {
          hookData.cell.styles.fontStyle = "bold";
        }
      },

      didDrawPage: (data) => {
        // =================================================
        // PAGE NUMBER
        // =================================================

        const pageCount = doc.internal.getNumberOfPages();

        const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;

        doc.setFontSize(8);

        doc.text(
          `Page ${pageCurrent} of ${pageCount}`,
          pageWidth - 10,
          doc.internal.pageSize.getHeight() - 5,
          {
            align: "right",
          },
        );
      },
    });

    // =====================================================
    // TOTAL SUMMARY
    // =====================================================

    const finalY = doc.lastAutoTable.finalY + 8;

    doc.setFontSize(10);

    doc.setFont(undefined, "bold");

    doc.text(`Total Debit: ${formatNumber(totalDebit)}`, 20, finalY);

    doc.text(`Total Credit: ${formatNumber(totalCredit)}`, 110, finalY);

    doc.setFont(undefined, "normal");

    // =====================================================
    // OPEN PDF IN NEW TAB
    // =====================================================

    const pdfBlob = doc.output("blob");

    const pdfUrl = URL.createObjectURL(pdfBlob);

    window.open(pdfUrl, "_blank");
  };

  // =========================================================
  // JSX
  // =========================================================

  return (
    <>
      <div className="flex bg-black main">
        <SideBar />

        <div className="h-full flex flex-col content-center w-full">
          <Header />

          <div className="flex justify-center mt-32">
            <div className="meterTableMain flex flex-col justify-items-center justify-center w-1/2 ml-10 mt-5 mr-10">
              {/* =================================================
                  HEADING
              ================================================= */}

              <div className="bg-blue-500 headingBox flex justify-center">
                <h1 className="text-white">TRIAL BALANCE</h1>
              </div>

              {/* =================================================
                  FORM
              ================================================= */}

              <div className="flex flex-col h-40 content-center items-center justify-items-center justify-evenly">
                {/* =================================================
                    DATES
                ================================================= */}

                <div>
                  <label className="text-white ml-5" htmlFor="endingDate">
                    Ending Date:
                  </label>
                  <input
                    type="date"
                    id="endingDate"
                    name="endingDate"
                    value={endingDate}
                    className="text-black inpTxt p-2 h-8 ml-3 mb-3"
                    onChange={setEndDate}
                  />
                </div>

                {/* =================================================
                    BUTTONS
                ================================================= */}

                <div>
                  <button
                    className={`bg-blue-500 text-white mt-2 ml-3 py-1 px-4 rounded ${
                      loading
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-blue-700"
                    }`}
                    onClick={editHandler}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "View"}
                  </button>

                  <button
                    onClick={clearFields}
                    disabled={loading}
                    className={`bg-blue-500 text-white mt-2 ml-3 py-1 px-4 rounded ${
                      loading
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-blue-700"
                    }`}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TrialBalance;
